import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { Entity } from "@common/types/entity";
import {
    AccountMergeStatus,
    PreviewAccountChange,
    PreviewAccountOrigin,
} from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { ChartOfAccountsRow } from "@components/accounting/chartOfAccounts/utils";
import coaStyles from "@components/accounting/chartOfAccounts/styles.module.scss";
import { PREVIEW_MISSING_CODE } from "../applyCoaTemplatePreviewMapper";
import { isArchivedForMode } from "../applyCoaPreviewArchivedState";
import {
    CoaPreviewMode,
    getAddedAccountTooltip,
    getAddedGroupTooltip,
    getArchivedTooltip,
    getMovedTooltip,
    getReassignedTooltip,
} from "../applyCoaPreviewTagCopy";
import styles from "./ApplyCoaPreviewNameCell.module.scss";

// Unlike the live `AccountNameCell`, this cell intentionally omits the
// click-to-copy account-id affordance: preview "new" accounts carry temp ids
// (e.g. "new:<templateAccountId>"), so a copied id would be meaningless. Do not
// "restore" the copy behaviour here without resolving that.

// Per-account preview metadata, keyed by the preview account id (matches
// `row.original.account.id`). Passed via tableMeta so the cell can render the
// Added / Reassigned / Moved / Archived tags and the code-clash (old → new)
// prefix without mutating the shared live-CoA account types.
export interface PreviewAccountMeta {
    mergeStatus: AccountMergeStatus;
    previousCode: number | null;
    changes: PreviewAccountChange[];
    previousGroupPath: string | null;
    // Where the account starts in the template <> entity diff (drives the
    // Origin column: entity avatar / Template / On both).
    origin: PreviewAccountOrigin;
    wasArchivedByApply: boolean;
}

// The active entity being previewed, used by the Origin column to render the
// real company (name + avatar) for entity-sourced rows. Picked from `Entity`
// so it stays in sync with it.
export type PreviewOriginEntity = Pick<
    Entity,
    "id" | "name" | "iconColor" | "workspaceId" | "logo"
>;

// A single tag + its explanatory tooltip. Each tag on a row gets its own
// tooltip (per the apply-template UI proposal).
const PreviewTag = ({
    label,
    color,
    tooltip,
    outline = false,
}: {
    label: string;
    color: "accent" | "neutral";
    tooltip: string;
    outline?: boolean;
}) => (
    <Tooltip content={tooltip}>
        <span>
            <Tag color={color} size="sm" outline={outline}>
                {label}
            </Tag>
        </span>
    </Tooltip>
);

export interface ApplyCoaPreviewTableMeta extends ViewTableMeta<
    Record<string, never>,
    unknown
> {
    accountMetaById: Map<string, PreviewAccountMeta>;
    // Tense of the tag tooltips: the live wizard preview is future tense
    // ("will be added"), the historical Activity drawer is past tense
    // ("was added"). Defaults to preview so existing callers are unaffected.
    mode?: CoaPreviewMode;
    // Present only in the apply flow (multi-entity); the Origin column and this
    // field are omitted by the AddClient setup preview.
    originEntity?: PreviewOriginEntity;
    // Ids of groups the apply would create, so group rows can show the "Added"
    // tag. Empty for previews with no template<>entity diff.
    newGroupIds: Set<string>;
    groupOriginById?: Map<string, PreviewAccountOrigin>;
}

const NameContent = ({ children }: { children: React.ReactNode }) => (
    <div className={classNames(coaStyles.columnContent, coaStyles.nameColumn)}>
        {children}
    </div>
);

export const ApplyCoaPreviewNameCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ChartOfAccountsRow, unknown>) => {
        const showAccountNumbers = useShowAccountNumbers();
        const rowData = row.original;
        const accountsCount = rowData.accounts?.length;

        const predefinedGroup = rowData.section ?? rowData.predefinedType;
        if (predefinedGroup) {
            return (
                <ViewTableCell>
                    <NameContent>
                        {getPredefinedGroupLabel(predefinedGroup)}
                        {accountsCount ? (
                            <span className={coaStyles.counter}>
                                {accountsCount}
                            </span>
                        ) : null}
                    </NameContent>
                </ViewTableCell>
            );
        }

        if (rowData.classification) {
            return (
                <ViewTableCell>
                    <NameContent>
                        {rowData.classification}
                        {accountsCount ? (
                            <span className={coaStyles.counter}>
                                {accountsCount}
                            </span>
                        ) : null}
                    </NameContent>
                </ViewTableCell>
            );
        }

        if (rowData.group) {
            const meta = table.options.meta as
                | ApplyCoaPreviewTableMeta
                | undefined;
            const mode = meta?.mode ?? "preview";
            const isNewGroup = meta?.newGroupIds.has(rowData.group.id) ?? false;
            return (
                <ViewTableCell>
                    <NameContent>
                        <span className={styles.accountName}>
                            <span>
                                <span>{rowData.group.name}</span>
                                {accountsCount ? (
                                    <span className={coaStyles.counter}>
                                        {accountsCount}
                                    </span>
                                ) : null}
                            </span>
                            {isNewGroup && (
                                <PreviewTag
                                    label="Added"
                                    color="accent"
                                    tooltip={getAddedGroupTooltip(mode)}
                                />
                            )}
                        </span>
                    </NameContent>
                </ViewTableCell>
            );
        }

        if (rowData.account) {
            const meta = table.options.meta as
                | ApplyCoaPreviewTableMeta
                | undefined;
            const mode = meta?.mode ?? "preview";
            const accountMeta = meta?.accountMetaById.get(rowData.account.id);
            const changes = accountMeta?.changes ?? [];
            const isAdded = changes.includes(PreviewAccountChange.added);
            const isReassigned = changes.includes(
                PreviewAccountChange.reassigned,
            );
            // Ordering-only change surfaced by the apply-time ordering pass; it
            // has no `changes` entry, so it's derived from the merge status.
            const isMoved =
                accountMeta?.mergeStatus === AccountMergeStatus.moved;
            const previousGroupPath = accountMeta?.previousGroupPath ?? null;
            const reassignedTooltip = getReassignedTooltip(
                mode,
                previousGroupPath,
            );
            const previousCode = accountMeta?.previousCode ?? null;
            const isArchived = isArchivedForMode(
                {
                    isDisabled: rowData.account.isDisabled,
                    wasArchivedByApply:
                        accountMeta?.wasArchivedByApply ?? false,
                },
                mode,
            );
            const hasCode = rowData.account.code !== PREVIEW_MISSING_CODE;
            // When the merge changes the account's code (a clash bumps a new
            // template account to the first-available code), show the old code
            // struck through before the new code, with a tooltip explaining why.
            const codePrefix =
                showAccountNumbers && hasCode ? (
                    <>
                        {previousCode !== null ? (
                            <Tooltip
                                content={`${previousCode} is already used by an entity's GL account; Kick assigned the next available number`}
                            >
                                <span className={styles.changedCode}>
                                    <s className={styles.previousCode}>
                                        {previousCode}
                                    </s>{" "}
                                    {rowData.account.code}
                                </span>
                            </Tooltip>
                        ) : (
                            rowData.account.code
                        )}
                        {" - "}
                    </>
                ) : null;

            return (
                <ViewTableCell>
                    <NameContent>
                        <span className={styles.accountName}>
                            <span>
                                {codePrefix}
                                {rowData.account.name}
                            </span>
                            {/* Small pills, each with its own tooltip.
                            Added (filled accent) and Reassigned (accent outline)
                            reflect the real merge outcome; Moved (neutral,
                            ordering-only) and Archived (grey) can accompany
                            either. */}
                            {isAdded && (
                                <PreviewTag
                                    label="Added"
                                    color="accent"
                                    tooltip={getAddedAccountTooltip(mode)}
                                />
                            )}
                            {isReassigned && (
                                <PreviewTag
                                    label="Reassigned"
                                    color="accent"
                                    outline
                                    tooltip={reassignedTooltip}
                                />
                            )}
                            {isMoved && (
                                <PreviewTag
                                    label="Moved"
                                    color="neutral"
                                    tooltip={getMovedTooltip(mode)}
                                />
                            )}
                            {isArchived && (
                                <PreviewTag
                                    label="Archived"
                                    color="neutral"
                                    tooltip={getArchivedTooltip(mode)}
                                />
                            )}
                        </span>
                    </NameContent>
                </ViewTableCell>
            );
        }

        return <ViewTableCell />;
    },
);

ApplyCoaPreviewNameCell.displayName = "ApplyCoaPreviewNameCell";
